import { defaultFallbackImagePath, getOriginalSizeUrl } from "newui/utils/images";
import React, { useCallback, useEffect, useRef, useState } from "react";

type FallbackImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
	alt?: string;
	className?: string;
	// optional classnames to be applied when in fallback
	fallBackClassName?: string;
	// optional fallback image path (we hard-code a default one)
	fallbackSrc?: string;
	src: string;
	style?: string;
	// whether to use a large image placeholder or small (default)
	useLarge?: boolean;
};

const FallbackImage: React.FC<FallbackImageProps> = ({
	fallbackSrc,
	style,
	src,
	alt,
	className,
	fallBackClassName,
	useLarge = false,
	...props
}) => {
	const [imgSrc, setImgSrc] = useState(src);
	const [altSrc, setAltSrc] = useState(alt);
	const [fallbackClass, setFallbackClassName] = useState("");
	const fallbacksTried = useRef({ fallback: false, default: false });

	useEffect(() => {
		setImgSrc(src);
		setAltSrc(alt);
		setFallbackClassName("");
		fallbacksTried.current = { fallback: false, default: false };
	}, [src, alt]);

	const handleError = useCallback(() => {
		// Fallback alt text
		setAltSrc(`Image not available ${src}`);

		// First failure → try fallbackSrc if available
		if (!fallbacksTried.current.fallback && fallbackSrc) {
			setImgSrc(fallbackSrc);
			fallbacksTried.current.fallback = true;
			return;
		}

		// Try original size URL if no fallbackSrc
		if (!fallbacksTried.current.fallback && !fallbackSrc) {
			const originalUrl = getOriginalSizeUrl(src);
			if (originalUrl) {
				setImgSrc(originalUrl);
				fallbacksTried.current.fallback = true;
				return;
			}
		}

		// Next failure → try default fallback
		if (!fallbacksTried.current.default) {
			const defaultFallback = defaultFallbackImagePath(useLarge ? "lg" : "sm");
			setImgSrc(defaultFallback);
			fallbacksTried.current.default = true;

			if (fallBackClassName) {
				setFallbackClassName(fallBackClassName);
			}

			return;
		}

		// if default fails, do nothing more - the alt text will indicate the image is unavailable
	}, [src, fallbackSrc, fallBackClassName, useLarge]);

	return (
		<img
			src={imgSrc}
			alt={altSrc}
			className={`${className} ${fallbackClass}`}
			onError={handleError}
			style={style}
			{...props}
		/>
	);
};

export default FallbackImage;
